import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../services/api';
import { useSetPageHeader } from '../context/PageHeaderContext';
import {
  FolderIcon,
  PlusIcon,
  MagnifyingGlassIcon,
  CalendarIcon,
  UserGroupIcon,
  ChevronRightIcon,
  PencilIcon,
  TrashIcon,
  ArrowPathIcon,
  EllipsisVerticalIcon,
} from '@heroicons/react/24/outline';

interface Project {
  _id: string;
  name: string;
  description: string;
  createdAt: string;
  updatedAt: string;
  goals: Array<{
    title: string;
    status: 'planned' | 'in-progress' | 'completed' | 'on-hold';
  }>;
  version: number;
}

const ProjectList: React.FC = () => {
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalProjects, setTotalProjects] = useState(0);
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);

  // Debounce search term - wait 300ms after user stops typing
  useEffect(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    debounceTimerRef.current = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300);

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [searchTerm]);

  const loadProjects = useCallback(async () => {
    try {
      setLoading(true);
      setError('');

      const params = new URLSearchParams({
        page: currentPage.toString(),
        limit: '10',
        ...(debouncedSearchTerm && { search: debouncedSearchTerm }),
      });

      const response: any = await api.get(`/projects?${params}`);

      if (response.success) {
        setProjects(response.data);
        setTotalPages(response.pagination.pages);
        setTotalProjects(response.pagination.total);
      }
    } catch (error: any) {
      console.error('Error loading projects:', error);
      setError('Failed to load projects');
    } finally {
      setLoading(false);
    }
  }, [currentPage, debouncedSearchTerm]);

  // Fetch projects when debounced search term or page changes
  useEffect(() => {
    loadProjects();
  }, [loadProjects]);

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1); // Reset to first page when searching
  };

  const handleDeleteProject = async (projectId: string) => {
    if (!window.confirm('Are you sure you want to delete this project?')) {
      return;
    }

    try {
      await api.delete(`/projects/${projectId}`);
      loadProjects(); // Reload the list
    } catch (error: any) {
      console.error('Error deleting project:', error);
      alert('Failed to delete project');
    }
  };

  const getGoalsSummary = (goals: Project['goals']) => {
    const completed = goals.filter((g) => g.status === 'completed').length;
    const total = goals.length;
    return { completed, total };
  };

  // Set page header
  useSetPageHeader(
    'Projects',
    'Manage your AI-powered product roadmap projects',
    <Link
      to="/projects/new"
      className="inline-flex items-center px-4 py-2 border border-transparent rounded-lg shadow-sm text-sm font-semibold text-white bg-accent-500 hover:bg-accent-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent-500 transition"
    >
      <PlusIcon className="h-4 w-4 mr-2" />
      New Project
    </Link>
  );

  if (loading && projects.length === 0) {
    return (
      <div className="text-center py-16">
        <ArrowPathIcon className="mx-auto h-10 w-10 text-blue-400 animate-spin" />
        <p className="mt-4 text-base text-gray-500">Loading projects...</p>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <div className="text-red-800">{error}</div>
        </div>
      )}

      {/* Search and Filters */}
      <div className="bg-white shadow-2xl rounded-2xl border border-blue-100">
        <div className="px-8 py-8 bg-gradient-to-r from-gray-50 to-white rounded-2xl">
          {/* Search Header */}
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">
                Find Projects
              </h2>
              <p className="text-sm text-gray-500 mt-1">
                Search through {totalProjects} projects
              </p>
            </div>
            <div className="text-sm text-gray-400 bg-gray-100 px-3 py-1 rounded-full">
              {projects.length} shown
            </div>
          </div>

          {/* Enhanced Search Bar */}
          <div className="relative group">
            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
              <MagnifyingGlassIcon className="h-5 w-5 text-gray-400 group-focus-within:text-accent-500 transition-colors duration-200" />
            </div>
            <input
              id="search"
              name="search"
              className="block w-full pl-12 pr-12 py-4 border-2 border-gray-200 rounded-xl leading-5 bg-white placeholder-gray-400 text-gray-900 transition-all duration-200 ease-in-out focus:outline-none focus:placeholder-gray-300 focus:ring-0 focus:border-accent-400 focus:bg-white hover:border-gray-300 shadow-sm focus:shadow-md"
              placeholder="Search projects by name or description..."
              type="search"
              value={searchTerm}
              onChange={handleSearch}
            />
            {searchTerm && (
              <button
                onClick={() => {
                  setSearchTerm('');
                  setCurrentPage(1);
                }}
                className="absolute inset-y-0 right-0 pr-4 flex items-center text-gray-400 hover:text-gray-600 transition-colors duration-200"
                aria-label="Clear search"
              >
                <svg
                  className="h-5 w-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            )}
          </div>

          {/* Search Results Summary */}
          {searchTerm && (
            <div className="mt-4 flex items-center justify-between text-sm">
              <div className="flex items-center space-x-2">
                <span className="text-gray-500">Search results for:</span>
                <span className="bg-secondary-100 text-secondary-800 px-2 py-1 rounded-md font-medium">
                  "{searchTerm}"
                </span>
              </div>
              {projects.length === 0 ? (
                <span className="text-red-600 font-medium">
                  No matches found
                </span>
              ) : (
                <span className="text-green-600 font-medium">
                  {projects.length} project{projects.length !== 1 ? 's' : ''}{' '}
                  found
                </span>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Projects Grid/List */}
      {projects.length === 0 && !loading ? (
        <div className="text-center py-12 bg-white shadow-2xl rounded-2xl border border-blue-100">
          <FolderIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">
            No projects found
          </h3>
          <p className="mt-1 text-sm text-gray-500">
            {searchTerm
              ? 'Try adjusting your search terms.'
              : 'Get started by creating a new project.'}
          </p>
          <div className="mt-6">
            <Link
              to="/projects/new"
              className="inline-flex items-center px-5 py-2 border border-transparent rounded-lg shadow-md text-base font-semibold text-white bg-accent-500 hover:bg-accent-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent-500 transition"
            >
              <PlusIcon className="h-5 w-5 mr-2" />
              New Project
            </Link>
          </div>
        </div>
      ) : (
        <div className="bg-white shadow-2xl rounded-2xl border border-blue-100">
          <ul className="divide-y divide-gray-100">
            {projects.map((project) => {
              const goalsSummary = getGoalsSummary(project.goals);
              return (
                <li key={project._id}>
                  <div className="px-4 py-3 sm:px-8 sm:py-6 flex items-center justify-between hover:bg-blue-50/60 transition-colors duration-200 group">
                    <div className="flex items-center min-w-0 flex-1">
                      <div className="flex-shrink-0">
                        <div className="h-10 w-10 rounded-lg bg-secondary-100 flex items-center justify-center">
                          <FolderIcon className="h-6 w-6 text-secondary-600" />
                        </div>
                      </div>
                      <div className="min-w-0 flex-1 px-4">
                        <div className="flex items-center justify-between">
                          <div className="min-w-0 flex-1">
                            <Link
                              to={`/projects/${project._id}`}
                              className="text-base font-medium text-primary-700 truncate hover:text-accent-600"
                            >
                              {project.name}
                            </Link>
                            <p className="text-sm text-gray-500 truncate mt-1">
                              {project.description}
                            </p>
                          </div>
                        </div>
                        <div className="mt-2 flex items-center text-sm text-gray-500 space-x-4">
                          <div className="hidden min-[800px]:flex items-center">
                            <CalendarIcon className="flex-shrink-0 mr-1.5 h-4 w-4 text-gray-400" />
                            Updated{' '}
                            {new Date(project.updatedAt).toLocaleDateString()}
                          </div>
                          <div className="hidden min-[800px]:flex items-center">
                            <UserGroupIcon className="flex-shrink-0 mr-1.5 h-4 w-4 text-gray-400" />
                            {goalsSummary.completed}/{goalsSummary.total}{' '}
                            goals completed
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Desktop Actions */}
                    <div className="hidden min-[800px]:flex items-center space-x-2">
                      <Link
                        to={`/projects/${project._id}/roadmaps`}
                        className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 hover:bg-green-200"
                      >
                        Roadmap
                      </Link>

                      <div className="relative flex items-center space-x-1">
                        <Link
                          to={`/projects/${project._id}/edit`}
                          className="p-1 text-gray-400 hover:text-gray-600"
                          title="Edit project"
                        >
                          <PencilIcon className="h-4 w-4" />
                        </Link>
                        <button
                          onClick={() => handleDeleteProject(project._id)}
                          className="p-1 text-gray-400 hover:text-red-600"
                          title="Delete project"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                        <Link
                          to={`/projects/${project._id}`}
                          className="p-1 text-gray-400 hover:text-gray-600"
                        >
                          <ChevronRightIcon className="h-5 w-5" />
                        </Link>
                      </div>
                    </div>

                    {/* Mobile Three-Dot Menu */}
                    <div className="min-[800px]:hidden relative">
                      <button
                        onClick={() => setOpenMenuId(openMenuId === project._id ? null : project._id)}
                        className="p-2 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100"
                        aria-label="More options"
                      >
                        <EllipsisVerticalIcon className="h-5 w-5" />
                      </button>

                      {openMenuId === project._id && (
                        <>
                          {/* Backdrop to close menu */}
                          <div
                            className="fixed inset-0 z-10"
                            onClick={() => setOpenMenuId(null)}
                          />

                          {/* Dropdown Menu */}
                          <div className="absolute right-0 mt-2 w-48 rounded-lg shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-20">
                            <div className="py-1">
                              <Link
                                to={`/projects/${project._id}`}
                                className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                onClick={() => setOpenMenuId(null)}
                              >
                                <ChevronRightIcon className="h-4 w-4 mr-3 text-gray-400" />
                                View Details
                              </Link>
                              <Link
                                to={`/projects/${project._id}/roadmaps`}
                                className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                onClick={() => setOpenMenuId(null)}
                              >
                                <FolderIcon className="h-4 w-4 mr-3 text-gray-400" />
                                Roadmap
                              </Link>
                              <Link
                                to={`/projects/${project._id}/edit`}
                                className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                onClick={() => setOpenMenuId(null)}
                              >
                                <PencilIcon className="h-4 w-4 mr-3 text-gray-400" />
                                Edit
                              </Link>
                              <button
                                onClick={() => {
                                  setOpenMenuId(null);
                                  handleDeleteProject(project._id);
                                }}
                                className="flex items-center w-full px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                              >
                                <TrashIcon className="h-4 w-4 mr-3" />
                                Delete
                              </button>
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      )
      }

      {/* Pagination */}
      {
        totalPages > 1 && (
          <div className="bg-white shadow-2xl rounded-2xl border border-blue-100 px-6 py-4 flex items-center justify-between">
            <div className="flex-1 flex justify-between sm:hidden">
              <button
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-base font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <button
                onClick={() =>
                  setCurrentPage(Math.min(totalPages, currentPage + 1))
                }
                disabled={currentPage === totalPages}
                className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-base font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700">
                  Showing{' '}
                  <span className="font-medium">
                    {(currentPage - 1) * 10 + 1}
                  </span>{' '}
                  to{' '}
                  <span className="font-medium">
                    {Math.min(currentPage * 10, totalProjects)}
                  </span>{' '}
                  of <span className="font-medium">{totalProjects}</span>{' '}
                  results
                </p>
              </div>
              <div>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                  <button
                    onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1}
                    className="relative inline-flex items-center px-3 py-2 rounded-l-lg border border-gray-300 bg-white text-base font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Previous
                  </button>

                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNum: number;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (currentPage >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = currentPage - 2 + i;
                    }

                    return (
                      <button
                        key={pageNum}
                        onClick={() => setCurrentPage(pageNum)}
                        className={`relative inline-flex items-center px-4 py-2 border text-base font-medium rounded-none ${pageNum === currentPage
                          ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                          : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                          }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}

                  <button
                    onClick={() =>
                      setCurrentPage(Math.min(totalPages, currentPage + 1))
                    }
                    disabled={currentPage === totalPages}
                    className="relative inline-flex items-center px-3 py-2 rounded-r-lg border border-gray-300 bg-white text-base font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
                </nav>
              </div>
            </div>
          </div>
        )
      }

      {
        loading && projects.length > 0 && (
          <div className="text-center py-4">
            <ArrowPathIcon className="mx-auto h-10 w-10 text-blue-400 animate-spin" />
            <p className="mt-4 text-base text-gray-500">Loading more projects...</p>
          </div>
        )
      }

      {/* Floating Action Button for Mobile/Tablet */}
      <Link
        to="/projects/new"
        className="min-[700px]:hidden fixed bottom-6 right-6 z-30 inline-flex items-center justify-center w-14 h-14 rounded-full shadow-lg text-white bg-accent-500 hover:bg-accent-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent-500 transition-all hover:scale-110"
        aria-label="New Project"
      >
        <PlusIcon className="h-6 w-6" />
      </Link>
    </div >
  );
};

export default ProjectList;

